import { useAccountContext } from '@/context/account'
import { useMixpanel } from '@/context/mixpanel'
import { cn } from '@/utils/cn'
import { type ReactNode, useState } from 'react'
import { NavLink, useLocation } from 'react-router'
import { Button } from '../common/button'
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogContentWrapper,
  DialogFooter,
  DialogHeader,
  DialogHeaderWrapper,
  DialogScrollArea,
  DialogTitle,
  DialogTrigger,
} from '../common/dialog'
import { Separator } from '../common/separator'
import { ConnectModal } from '../connect/connectModal'
import { ConnectionMenu } from '../connect/connectionMenu'
import { DocsIcon } from '../icons/docs'
import { HelpIcon } from '../icons/help'
import { MenuIcon } from '../icons/menu'
import { TerminalIcon } from '../icons/terminal'
import { XIcon } from '../icons/x'
import { SettingsMenu } from '../settings/settingsMenu'
import { MENU_LINKS, headerLinks, isMenuLinkActive } from './header'

export const MobileMenu = () => {
  const { account } = useAccountContext()
  const [open, setOpen] = useState(false)
  const { trackEvent } = useMixpanel()
  const { pathname } = useLocation()

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button
          variant="default"
          size="default"
          className="size-10 min-w-8 rounded-xl bg-page px-1 text-accent xl:hidden"
        >
          <MenuIcon size={24} />
        </Button>
      </DialogTrigger>
      <DialogContent position="right" className="max-w-80">
        <DialogBody>
          <DialogHeaderWrapper>
            <DialogHeader>
              <DialogTitle>Navigation</DialogTitle>
            </DialogHeader>
            <Separator />
          </DialogHeaderWrapper>
          <DialogScrollArea>
            <DialogContentWrapper className="pt-4">
              <ul className="flex flex-col gap-4 md:gap-5">
                {MENU_LINKS.map(({ name, icon, to, activePaths }) => (
                  <li key={name + to}>
                    <Button variant="input" size="default" asChild>
                      <NavLink
                        to={to}
                        className={cn(
                          'h-10 w-full gap-2 bg-page px-4',
                          '[&.active]:border-primary-700 [&.active]:bg-accent [&.active]:text-neutral-50',
                          isMenuLinkActive(pathname, activePaths) && 'active'
                        )}
                        onClick={() => setOpen(false)}
                      >
                        <span className="ml-0.5">{icon}</span>
                        <span className="font-semibold text-sm">{name}</span>
                      </NavLink>
                    </Button>
                  </li>
                ))}
              </ul>
              <Separator />
              <SettingsMenu className="bg-page" />
            </DialogContentWrapper>
          </DialogScrollArea>
          <DialogFooter className="bg-page">
            <nav className="flex w-full flex-col gap-4">
              <div className="mx-auto flex items-start gap-4">
                <SocialLink href={headerLinks.twitter} icon={<XIcon />} label="Twitter" />
                <SocialLink href={headerLinks.legal} icon={<DocsIcon />} label="Legal" />
              </div>
              <Separator />
              <Button variant="input" size="md" className="gap-2 font-normal" asChild>
                <a href={headerLinks.api}>
                  <TerminalIcon />
                  API
                </a>
              </Button>
              <Button variant="input" size="md" className="gap-2 font-normal" asChild>
                <a href={headerLinks.telegram}>
                  <HelpIcon />
                  Contact support
                </a>
              </Button>
              {account ? (
                <ConnectionMenu className="w-full" account={account} modal={true} />
              ) : (
                <>
                  <ConnectModal
                    action="connect"
                    onClick={() => trackEvent('Mobile menu connect wallet button clicked')}
                  />
                  <ConnectModal
                    action="create"
                    onClick={() => trackEvent('Mobile menu create wallet button clicked')}
                  />
                </>
              )}
            </nav>
          </DialogFooter>
        </DialogBody>
      </DialogContent>
    </Dialog>
  )
}

interface SocialLinkProps {
  href: string
  icon: ReactNode
  label: string
}

const SocialLink = ({ href, icon, label }: SocialLinkProps) => (
  <Button variant="default" size="default" asChild>
    <a href={href} target="_blank" rel="noreferrer">
      {icon}
      <span className="sr-only">{label}</span>
    </a>
  </Button>
)
